import React, { useState, useEffect } from 'react';
import { Table, Button, Input, Space, Tag, Typography, Popconfirm, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PlusOutlined, SearchOutlined, EditOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import api from '../../services/api';
import { ReviewTask } from '../../types';

const { Title } = Typography;

const ReviewTaskList: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState<ReviewTask[]>([]);
  const [searchText, setSearchText] = useState('');

  // 獲取測評任務數據
  const fetchTasks = async () => {
    setLoading(true);
    try {
      const data = await api.reviewTasks.getAll();
      setTasks(data);
    } catch (error) {
      console.error('Error fetching review tasks:', error);
      message.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTasks();
  }, []);

  // 刪除測評任務
  const handleDelete = async (id: string) => {
    try {
      await api.reviewTasks.delete(id);
      message.success(t('common.success'));
      fetchTasks();
    } catch (error) {
      console.error('Error deleting review task:', error);
      message.error(t('common.error'));
    }
  };

  // 表格列定義
  const columns: ColumnsType<ReviewTask> = [
    {
      title: t('reviewTasks.taskId'),
      dataIndex: 'task_id',
      key: 'task_id',
      sorter: (a: ReviewTask, b: ReviewTask) => a.task_id.localeCompare(b.task_id),
      filteredValue: searchText ? [searchText] : null,
      onFilter: (value: string | number | boolean, record: ReviewTask): boolean => {
        const searchValue = String(value).toLowerCase();
        return Boolean(
          record.task_id.toLowerCase().includes(searchValue) ||
          record.store_id.toLowerCase().includes(searchValue) ||
          (record.review_account && record.review_account.toLowerCase().includes(searchValue))
        );
      },
    },
    {
      title: t('reviewTasks.storeId'),
      dataIndex: 'store_id',
      key: 'store_id',
    },
    {
      title: t('reviewTasks.platform'),
      dataIndex: 'platform',
      key: 'platform',
      filters: Array.from(new Set(tasks.map(task => task.platform))).map(platform => ({
        text: platform,
        value: platform,
      })),
      onFilter: (value: string | number | boolean, record: ReviewTask) => record.platform === String(value),
    },
    {
      title: t('reviewTasks.date'),
      dataIndex: 'date',
      key: 'date',
      sorter: (a: ReviewTask, b: ReviewTask) => new Date(a.date).getTime() - new Date(b.date).getTime(),
    },
    {
      title: t('reviewTasks.amount'),
      dataIndex: 'amount',
      key: 'amount',
      sorter: (a: ReviewTask, b: ReviewTask) => a.amount - b.amount,
    },
    {
      title: t('reviewTasks.reviewStatus'),
      dataIndex: 'review_status',
      key: 'review_status',
      render: (status: string) => (
        <Tag color={status === '已評論' ? 'success' : 'warning'}>
          {status}
        </Tag>
      ),
      filters: [
        { text: t('reviewTasks.reviewStatuses.reviewed'), value: '已評論' },
        { text: t('reviewTasks.reviewStatuses.unreviewed'), value: '未評論' },
      ],
      onFilter: (value: string | number | boolean, record: ReviewTask) => record.review_status === String(value),
    },
    {
      title: t('reviewTasks.deliveryStatus'),
      dataIndex: 'delivery_status',
      key: 'delivery_status',
      render: (status: string) => (
        <Tag color={status === '已收貨' ? 'success' : 'warning'}>
          {status}
        </Tag>
      ),
      filters: [
        { text: t('reviewTasks.deliveryStatuses.received'), value: '已收貨' },
        { text: t('reviewTasks.deliveryStatuses.unreceived'), value: '未收貨' },
      ],
      onFilter: (value: string | number | boolean, record: ReviewTask) => record.delivery_status === String(value),
    },
    {
      title: t('common.action'),
      key: 'action',
      render: (_: any, record: ReviewTask) => (
        <Space size="middle">
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => navigate(`/review-tasks/${record.id}`)}
          />
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => navigate(`/review-tasks/${record.id}/edit`)}
          />
          <Popconfirm
            title={t('reviewTasks.confirmDelete')}
            onConfirm={() => handleDelete(record.id)}
            okText={t('common.yes')}
            cancelText={t('common.no')}
          >
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <Title level={2}>{t('reviewTasks.title')}</Title>
        <Space>
          <Input
            placeholder={t('common.search')}
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 200 }}
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate('/review-tasks/new')}
          >
            {t('reviewTasks.addTask')}
          </Button>
        </Space>
      </div>
      <Table
        columns={columns}
        dataSource={tasks}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 10 }}
      />
    </div>
  );
};

export default ReviewTaskList;
